'use client';

import { INITIAL_LAYOUT_STATE, layoutReducer } from 'BomdisoftComponents/layout/reducer';
import { layoutState, toInitialLayoutState } from 'BomdisoftComponents/layout/state';
import type { LayoutMachineContext, LayoutMachineEvents } from 'BomdisoftComponents/layout/types';
import { createContext, type Dispatch, type ReactNode, useContext, useEffect, useReducer } from 'react';

export { layoutState } from 'BomdisoftComponents/layout/state';

const LayoutStateContext = createContext<LayoutMachineContext | null>(null);
const LayoutDispatchContext = createContext<Dispatch<LayoutMachineEvents> | null>(null);

export const LayoutProvider = ({ children, defaultSidebarOpen = false }: { children: ReactNode; defaultSidebarOpen?: boolean }) => {
  /* Seeded from storage during the first render rather than in an effect, so a
     visitor who collapsed the sidebar never sees it flash open first. The
     declared default applies only when nothing was ever stored. */
  const [state, dispatch] = useReducer(layoutReducer, defaultSidebarOpen, toInitialLayoutState);

  useEffect(() => {
    layoutState.write(state);
  }, [state]);

  return (
    <LayoutStateContext.Provider value={state}>
      <LayoutDispatchContext.Provider value={dispatch}>{children}</LayoutDispatchContext.Provider>
    </LayoutStateContext.Provider>
  );
};

export const useLayoutState = <T,>(select: (state: LayoutMachineContext) => T): T => {
  const state = useContext(LayoutStateContext);
  return select(state ?? INITIAL_LAYOUT_STATE);
};

export const useLayoutDispatch = (): Dispatch<LayoutMachineEvents> => {
  const dispatch = useContext(LayoutDispatchContext);
  // A no-op keeps a component rendered outside the provider inert rather than
  // throwing — the same tolerance the actor context had.
  return dispatch ?? (() => undefined);
};
